import React, { useState } from "react";
import ExpandableCostCard from "../../components/ExpandableCostCard";
import API_BASE_URL from "../../services/apiClient";
import DayAtmosphereGallery from "./DayAtmosphereGallery";

// --- HELPERS FOR METADATA ---
const getActivityChips = (act) => {
  const text = `${act.description} ${act.location || ""}`.toLowerCase();
  const chips = [];
  if (text.includes("walk") || text.includes("stroll") || text.includes("park") || text.includes("hike")) chips.push("Walking Friendly");
  if (text.includes("dinner") || text.includes("lunch") || text.includes("food") || text.includes("cafe") || text.includes("restaurant") || text.includes("eat")) chips.push("Culinary");
  if (text.includes("sunset") || text.includes("view") || text.includes("scenic") || text.includes("photo")) chips.push("Great Views");
  if (text.includes("museum") || text.includes("art") || text.includes("history") || text.includes("gallery") || text.includes("temple")) chips.push("Culture");
  if (text.includes("shop") || text.includes("market") || text.includes("mall") || text.includes("boutique")) chips.push("Local Vibe");
  if (text.includes("relax") || text.includes("beach") || text.includes("spa") || text.includes("chill")) chips.push("Calm Atmosphere");
  return chips.slice(0, 2);
};

const HoursItinerary = ({
  data,
  city,
  costEstimate,
  hideHeader = false,
  editable = false,
  travelKeyToken,
  refreshTrip
}) => {

  const [showInput, setShowInput] = useState(false);
  const [newActivity, setNewActivity] = useState("");

  if (!data?.days?.length) {
    return (
      <div className="py-32 flex flex-col items-center justify-center text-center animate-in fade-in duration-1000">
        <div className="w-16 h-16 bg-gray-50/50 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-gray-100/50 backdrop-blur-sm">
          <span className="text-2xl opacity-40">✧</span>
        </div>
        <p className="text-gray-400 font-medium tracking-wide text-sm">
          No itinerary available yet
        </p>
      </div>
    );
  }

  const day = data.days[0];

  // 🍜 FOOD FUNCTION
  const openFoodNearby = (place) => {
    const query = encodeURIComponent(`restaurants near ${place}, ${city}`);
    window.open(`https://www.google.com/maps/search/${query}`, "_blank");
  };

  // 🗺 ROUTE FUNCTION
  const openDayRoute = (places) => {
    if (places.length < 2) return;

    const origin = places[0];
    const destination = places[places.length - 1];
    const waypoints =
      places.length > 2 ? places.slice(1, -1).join("|") : "";

    const url =
      `https://www.google.com/maps/dir/?api=1` +
      `&origin=${encodeURIComponent(origin)}` +
      `&destination=${encodeURIComponent(destination)}` +
      (waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : "");

    window.open(url, "_blank");
  };

  const normalizeSections = () => {
    if (day.sections?.length) return day.sections;

    return [
      {
        period: "Hourly Plan",
        activities: (day.activities || []).map((item) => ({
          description:
            typeof item === "string" ? item : item?.description,
          location: item?.location || null,
        })),
      },
    ];
  };

  const sections = normalizeSections();

  const transportSection = sections.find((s) =>
    s.period?.toLowerCase().includes("transport")
  );

  const hours = sections
    .filter(
      (s) =>
        !s.period?.toLowerCase().includes("transport") &&
        !s.period?.toLowerCase().includes("budget")
    )
    .flatMap((section) =>
      (section.activities || []).map((act) => ({
        description: act?.description,
        location: act?.location,
      }))
    );

  // 👉 extract all locations for route
  const allLocations = hours
    .map((item) => item.location)
    .filter(Boolean);

  const handleAddActivity = async () => {
    if (!newActivity) return;

    try {
      const token =
        sessionStorage.getItem("token") ||
        localStorage.getItem("token");

      await fetch(
        `${API_BASE_URL}/api/trips/${travelKeyToken}/activity`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            dayIndex: 0,
            sectionIndex: 0,
            description: newActivity
          })
        }
      );

      setNewActivity("");
      setShowInput(false);

      await refreshTrip();

    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-16 animate-in fade-in slide-in-from-bottom-4 duration-1000 ease-out">

      {/* HEADER */}
      {!hideHeader && city && (
        <div className="pb-12 border-b border-black/5 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 relative">

          <div>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 tracking-tight leading-tight mb-3">
              {city}
            </h2>
            <p className="text-gray-400 font-bold text-[11px] uppercase tracking-[0.2em] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5b6f00]/50 animate-pulse"></span>
              Hour-by-Hour Plan
            </p>
          </div>

          {allLocations.length >= 2 && (
            <button
              onClick={() => openDayRoute(allLocations)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-white/80 backdrop-blur-sm border border-gray-100 shadow-sm rounded-full text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:shadow transition-all duration-300"
            >
              <span className="opacity-50">🗺</span> View Route
            </button>
          )}

        </div>
      )}

      {costEstimate && (
        <div className="mb-6">
          <ExpandableCostCard cost={costEstimate} />
        </div>
      )}

      {/* ACTIVITIES */}
      <div className="space-y-10 relative">

        <div className="absolute left-[38px] sm:left-[50px] top-4 bottom-4 w-[1px] bg-gradient-to-b from-transparent via-gray-200 to-transparent -z-10 hidden sm:block"></div>

        {hours.map((item, index) => {
          const chips = getActivityChips(item);
          return (
          <div
            key={index}
            className="relative flex flex-row sm:grid sm:grid-cols-[100px_1fr] gap-4 sm:gap-8 group"
          >
            {/* NUMBER COLUMN */}
            <div className="flex flex-col items-center sm:items-end gap-1 mb-0 relative z-10 sm:pr-8">
              <div className="hidden sm:flex absolute -right-[5px] top-[14px] w-[9px] h-[9px] rounded-full bg-white border-[2px] border-gray-200 group-hover:border-[#5b6f00] transition-colors duration-500 shadow-sm z-20"></div>
              
              <div className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tighter leading-none group-hover:text-[#5b6f00] transition-colors duration-500 w-8 sm:w-auto text-center sm:text-right">
                {index + 1}
              </div>
            </div>

            {/* CONTENT COLUMN */}
            <div className="flex-1 relative bg-white rounded-3xl p-6 sm:p-8 border border-gray-100/80 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.06)] transition-all duration-500 group/act hover:border-gray-200">
              <p className="text-[15px] sm:text-lg font-medium text-gray-800 leading-relaxed group-hover/act:text-gray-900 transition-colors">
                {item.description}
              </p>

              {item.type === "user" && item.addedBy && (
                <p className="text-[10px] text-gray-400 mt-2 font-medium">
                  ✎ Added by {item.addedBy.name || item.addedBy.username}
                </p>
              )}

              {(item.location || chips.length > 0) && (
                <div className="mt-4 flex items-center gap-2 flex-wrap">

                  {item.location && (
                    <div className="text-[11px] font-semibold text-gray-700 bg-white border border-gray-200/60 shadow-sm px-3 py-1.5 rounded-full flex items-center gap-1.5">
                      <span className="text-gray-400">📍</span> {item.location}
                    </div>
                  )}

                  {chips.map((chip, cIdx) => (
                    <div key={cIdx} className="text-[10px] font-medium text-gray-500 bg-gray-50/80 px-2.5 py-1.5 rounded-full">
                      ✧ {chip}
                    </div>
                  ))}

                  {item.location && (
                    <button
                      onClick={() => openFoodNearby(item.location)}
                      className="text-[11px] font-semibold bg-[#5b6f00]/5 text-[#5b6f00] px-3 py-1.5 rounded-full hover:bg-[#5b6f00]/10 transition-colors ml-auto sm:ml-0"
                    >
                      🍜 Food Nearby
                    </button>
                  )}
                </div>
              )}

              {/* ADD ACTIVITY */}
              {editable && index === hours.length - 1 && (
                <div className="mt-6 pt-4 border-t border-gray-100/50">
                  {showInput ? (
                    <div className="flex gap-2 animate-in fade-in zoom-in-95 duration-200">
                      <input
                        value={newActivity}
                        onChange={(e) => setNewActivity(e.target.value)}
                        placeholder="Add a new activity to this plan..."
                        className="border border-gray-200 px-4 py-2.5 rounded-xl w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00]/50 transition-all shadow-sm"
                        autoFocus
                      />
                      <button
                        onClick={handleAddActivity}
                        className="bg-[#5b6f00] text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-md shadow-[#5b6f00]/20 hover:shadow-lg hover:shadow-[#5b6f00]/30 transition-all active:scale-95"
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowInput(true)}
                      className="text-xs text-gray-400 hover:text-[#5b6f00] font-semibold flex items-center gap-1.5 transition-colors"
                    >
                      <span className="text-lg leading-none">+</span> Add Activity
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )})}
      </div>

      <DayAtmosphereGallery
        city={city}
        activities={hours.map(h => h.description + " " + (h.location || ""))}
        isMinimal={true}
      />

      {/* TRANSPORT */}
      {transportSection && (
        <div className="mt-12 bg-gradient-to-br from-gray-900 to-gray-800 rounded-[32px] p-8 sm:p-10 border border-gray-800 flex flex-col md:flex-row gap-8 items-center justify-between shadow-xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 mix-blend-overlay"></div>
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/5 rounded-full blur-3xl group-hover:bg-white/10 transition-colors duration-700"></div>
          <div className="max-w-md relative z-10">

            <div className="bg-white/10 text-white/80 backdrop-blur-md w-fit px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-[0.2em] mb-5 border border-white/10">
              Logistics
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight">
              Transport & Arrival
            </h3>

            {(transportSection.activities || []).map((act, i) => (
              <p key={i} className="text-gray-300 text-[15px] leading-relaxed font-medium">
                {act.description}
              </p>
            ))}

          </div>

          {/* <div className="text-7xl opacity-20 filter drop-shadow-2xl transform group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-700 relative z-10">
            🚕
          </div> */}
        </div>
      )}

    </div>
  );
};

export default HoursItinerary;